<template>
  <div class="fts-settings-page">
    <header class="fts-settings-head">
      <div>
        <h1>Billing &amp; invoices</h1>
        <p>The card we charge, what is due next, and every invoice and receipt.</p>
      </div>
    </header>

    <div class="fts-settings-loading" v-if="loading">
      <fts-spinner :size="16" />
      <span>Loading&hellip;</span>
    </div>

    <section class="fts-settings-card" v-else-if="unavailable">
      <h2>{{ unavailable.title }}</h2>
      <p class="fts-settings-card-sub">{{ unavailable.body }}</p>
    </section>

    <template v-else>
      <!-- A failed payment is the one thing on this page that needs acting on today, so it
           leads. AI is the only thing that pauses; records, forms and automations never do. -->
      <div class="fts-settings-banner is-danger" v-if="paymentFailed">
        <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
        <span>{{ paymentFailed }}</span>
      </div>

      <!-- Vendor-billed workspaces have no card and no invoices HERE: both live with the
           vendor. Every buying surface is replaced by who to ask. -->
      <section class="fts-settings-card" v-if="isManual">
        <h2>{{ billedByLabel }} bills this workspace</h2>
        <p class="fts-settings-card-sub">
          Invoices, payment details and plan changes go through them. Usage, people, security and
          vouchers all work here as usual.
        </p>
        <div class="fts-settings-row" v-if="summary.validUntil">
          <div class="fts-settings-row-text">
            <strong>Arrangement valid until</strong>
            <span>{{ date(summary.validUntil) }}</span>
          </div>
        </div>
      </section>

      <template v-else>
        <!-- Payment method. Stripe never gives us more than the brand, the last four and the
             expiry, and we never ask for more: card data does not touch this server. -->
        <section class="fts-settings-card">
          <div>
            <h2>Payment method</h2>
            <p class="fts-settings-card-sub">
              Card details are held by Stripe. Changing a card opens their secure page.
            </p>
          </div>

          <div class="fts-settings-row">
            <div class="fts-settings-row-text" v-if="card">
              <strong>{{ card.label }}</strong>
              <span :class="{ 'is-warn-text': card.expired || card.expiring }">{{ card.expiry }}</span>
            </div>
            <div class="fts-settings-row-text" v-else>
              <strong>{{ cardUnknown ? "Card on file" : "No card on file" }}</strong>
              <span>{{ noCardNote }}</span>
            </div>
            <button
              type="button"
              class="fts-settings-btn"
              :class="{ 'is-primary': (!card && !cardUnknown) || (card && card.expired) }"
              :disabled="busy"
              v-if="canManageCard"
              @click="openPortal('payment_method_update')"
            >
              {{ cardLabel }}
            </button>
          </div>
        </section>

        <!-- What is due next. The amount and the proration are Stripe's; this only reports
             them, which is why there is nothing to recalculate here. -->
        <section class="fts-settings-card">
          <div>
            <h2>Next invoice</h2>
            <p class="fts-settings-card-sub" v-if="nextInvoice">
              Estimated. Stripe issues the invoice on the day and it is the final word.
            </p>
          </div>

          <template v-if="nextInvoice">
            <div class="fts-settings-next">
              <div>
                <div class="fts-settings-eyebrow">Total</div>
                <div class="fts-settings-big">{{ nextInvoice.totalLabel }}</div>
              </div>
              <div class="fts-settings-next-when">
                <span v-if="nextInvoice.dateLabel">on {{ nextInvoice.dateLabel }}</span>
                <span v-if="nextInvoice.taxLabel" class="fts-settings-note">{{ nextInvoice.taxLabel }}</span>
              </div>
            </div>

            <table class="fts-settings-table" v-if="nextInvoice.lines.length">
              <tbody>
                <tr v-for="(line, i) in nextInvoice.lines" :key="i">
                  <td>{{ line.description }}</td>
                  <td class="is-right">{{ line.amountLabel }}</td>
                </tr>
              </tbody>
            </table>
          </template>

          <p class="fts-settings-note" v-else>{{ noNextInvoiceNote }}</p>
        </section>
      </template>

      <!-- Connector plans (docs/billing-autocount-connector-pricing-proposal.md §9, SVN): AutoCount
           Full is a line on the workspace's one subscription, so it is cancelled here, line by
           line; the portal's cancel would end the whole subscription. A vendor-billed workspace
           sees who bills it instead. -->
      <section class="fts-settings-card fts-billing-connectors" v-if="connectorRows.length">
        <div>
          <h2>Connectors</h2>
          <p class="fts-settings-card-sub">{{ connectorsNote }}</p>
        </div>
        <div class="fts-settings-row" v-for="row in connectorRows" :key="row.key">
          <div class="fts-settings-row-text">
            <strong>{{ row.name }}</strong>
            <span>{{ row.detail }}</span>
          </div>
          <button
            type="button"
            class="fts-settings-btn"
            :disabled="busy"
            v-if="row.cancellable"
            @click="cancelConnector(row)"
          >
            Cancel
          </button>
        </div>
      </section>

      <template v-if="!isManual">
        <!-- Invoices and receipts. Read from Stripe, where the status and the PDF live, rather
             than from a copy here that could disagree with them about money. -->
        <section class="fts-settings-card">
          <div>
            <h2>Invoices &amp; receipts</h2>
            <p class="fts-settings-card-sub">
              Subscription invoices and credit top-ups, newest first.
            </p>
          </div>

          <table class="fts-settings-table" v-if="documents.length">
            <thead>
              <tr>
                <th scope="col" class="is-date">Date</th>
                <th scope="col">Description</th>
                <th scope="col" class="is-amount">Amount</th>
                <th scope="col">Status</th>
                <th scope="col"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="doc in documents" :key="doc.key">
                <td class="is-date">{{ doc.dateLabel }}</td>
                <td>
                  {{ doc.description }}
                  <small v-if="doc.number">{{ doc.number }}</small>
                </td>
                <td class="is-amount">{{ doc.amountLabel }}</td>
                <td>
                  <span class="fts-settings-pill" :class="doc.tone">{{ doc.statusLabel }}</span>
                </td>
                <td class="is-right">
                  <a
                    v-if="doc.pdfUrl"
                    :href="doc.pdfUrl"
                    target="_blank"
                    rel="noopener"
                    class="fts-settings-link"
                  >
                    <font-awesome-icon :icon="['far', 'file-pdf']" /> PDF
                  </a>
                  <a
                    v-else-if="doc.hostedUrl"
                    :href="doc.hostedUrl"
                    target="_blank"
                    rel="noopener"
                    class="fts-settings-link"
                  >
                    <font-awesome-icon :icon="['fas', 'up-right-from-square']" /> View
                  </a>
                </td>
              </tr>
            </tbody>
          </table>

          <p class="fts-settings-note" v-else>{{ noDocumentsNote }}</p>
        </section>

        <!-- Region is frozen by the first completed checkout because it decides the currency
             every later charge is made in (pricing spec §7.1). Saying so is kinder than a
             disabled select with no explanation. -->
        <section class="fts-settings-card">
          <div>
            <h2>Region &amp; currency</h2>
            <p class="fts-settings-card-sub">{{ regionNote }}</p>
          </div>
          <template v-if="canChooseRegion">
            <fieldset class="fts-billing-country">
              <legend>Business country</legend>
              <div class="fts-billing-seg">
                <label
                  v-for="c in regionChoices"
                  :key="c.region"
                  class="fts-billing-seg-option"
                  :class="{ 'is-on': c.on }"
                >
                  <input
                    type="radio"
                    name="fts-billing-country"
                    :value="c.region"
                    :checked="c.on"
                    :disabled="busy"
                    @change="chooseRegion(c.region)"
                  />
                  <span>{{ c.label }}</span>
                </label>
              </div>
            </fieldset>
            <div class="fts-settings-row">
              <div class="fts-settings-row-text">
                <strong>Charged in {{ currencyLabel }}</strong>
                <span>{{ regionGuessLine }}</span>
              </div>
            </div>
          </template>
          <div class="fts-settings-row" v-else>
            <div class="fts-settings-row-text">
              <strong>{{ regionLabel }}</strong>
              <span>Charged in {{ currencyLabel }}</span>
            </div>
          </div>
        </section>

        <section class="fts-settings-card" v-if="canCancel">
          <div>
            <h2>Cancel plan</h2>
            <p class="fts-settings-card-sub">{{ cancelNote }}</p>
          </div>
          <div class="fts-settings-row">
            <div class="fts-settings-row-text">
              <strong>{{ cancelHeading }}</strong>
              <span>{{ cancelDetail }}</span>
            </div>
            <button
              type="button"
              class="fts-settings-btn"
              :disabled="busy || planCancelling"
              @click="cancelPlan"
            >
              {{ planCancelling ? "Already cancelling" : "Cancel plan" }}
            </button>
          </div>
        </section>
      </template>

      <!-- Who gets the billing emails. Shown for a vendor-billed workspace too: credit and limit
           warnings reach either kind. -->
      <settings-billing-contacts :workspace-id="workspaceId" />
    </template>
  </div>
</template>

<script>
/**
 * Settings › Billing & invoices (P1, proposal §3 D-6).
 *
 * Every figure here comes from Stripe through the server (§4 #1 and #5) and every EDIT leaves
 * for Stripe's own hosted page through a flow-targeted portal session (§4 #7). Nothing about a
 * card is entered, stored or recalculated here, and nothing on this page can change what is
 * charged -- which is why the next-invoice panel says "estimated" and defers to the invoice.
 *
 * Both new server fields are treated as optional throughout. They are absent on a server that
 * has not taken r33635 yet, absent for a vendor-billed workspace, and absent whenever a Stripe
 * read fails -- and a page that only works when every read succeeds is a page that breaks
 * during an outage.
 *
 * Billing contacts (§4 #6) are their own card, SettingsBillingContacts, with their own read and
 * save; the emails they receive are switched on separately once their wording is agreed.
 *
 * The exception is cancelling one line of the subscription (docs/billing-autocount-connector-
 * pricing-proposal.md §6.3, §6.5): AutoCount Full rides on the workspace's one subscription, and the
 * portal's cancel would end all of it. So AutoCount Full is cancelled here, and so is the plan
 * while AutoCount Full is on the subscription (or when the portal answers 409 review_in_app).
 */
import { commonStore } from "../../store/commonStore";
import { formatMoney } from "../../commons/billing";
import FTSSpinner from "../../elements/FTSSpinner";
import SettingsBillingContacts from "./SettingsBillingContacts";

const REGION_NAMES = { MY: "Malaysia", SG: "Singapore", US: "Other countries" };
const CURRENCY_NAMES = { myr: "Malaysian ringgit (RM)", sgd: "Singapore dollars (S$)", usd: "US dollars (US$)" };

/** The business countries a workspace admin picks from before the first payment (activation fee doc A-5a). */
const REGION_CHOICES = ["MY", "SG", "US"];

/** Choosing the country's refusals when the server sends a code without words. */
const REGION_REFUSALS = {
  region_confirmed: "The first payment fixed this workspace's currency. Talk to us if it needs to change.",
  dealer_billed: "Your vendor bills this workspace, so its currency is set with them.",
  bad_region: "Choose Malaysia, Singapore or Other countries.",
};

/** Stripe invoice status → what a customer would call it. */
const STATUS_LABELS = {
  paid: "Paid",
  open: "Due",
  uncollectible: "Written off",
  void: "Voided",
};

export default {
  name: "SettingsBilling",
  components: {
    "fts-spinner": FTSSpinner,
    "settings-billing-contacts": SettingsBillingContacts,
  },
  props: {
    workspaceId: [String, Number],
  },
  data() {
    return {
      loading: true,
      busy: false,
      unavailable: null,
      summary: null,
      documents: [],
      documentsFailed: false,
    };
  },
  computed: {
    isManual() {
      return !!this.summary && this.summary.source === "manual";
    },
    billedByLabel() {
      return (this.summary && this.summary.billedBy) || "Your vendor";
    },
    paymentFailed() {
      if (!this.summary) return "";
      const status = this.summary.status;
      if (status !== "past_due" && status !== "unpaid") return "";
      const grace = this.summary.graceUntil
        ? " AI keeps working until " + this.date(this.summary.graceUntil) + "."
        : "";
      return "A payment did not go through. Update the card below and Stripe will try again."
        + grace
        + " Records, forms and automations are never affected.";
    },
    card() {
      const pm = this.summary && this.summary.paymentMethod;
      if (!pm || !pm.last4) return null;

      const brand = pm.brand ? this.titleCase(pm.brand) : "Card";
      const month = Number(pm.expMonth) || 0;
      const year = Number(pm.expYear) || 0;
      let expiry = "No expiry on file";
      let expired = false;
      let expiring = false;

      if (month > 0 && year > 0) {
        // A card that expires this month still works this month, so the boundary is the END of
        // the expiry month -- calling it dead a few weeks early would send people to Stripe for
        // nothing.
        const endOfMonth = new Date(year, month, 1);
        const now = new Date();
        expired = endOfMonth <= now;
        expiring = !expired && (endOfMonth - now) < 1000 * 60 * 60 * 24 * 60;
        expiry = expired
          ? "Expired " + this.padMonth(month) + "/" + year
          : "Expires " + this.padMonth(month) + "/" + year;
      }

      return {
        label: brand + " ending " + pm.last4,
        expiry,
        expired,
        expiring,
      };
    },
    /**
     * "The server did not tell us" is not the same as "there is no card", and saying the second
     * when the first is true would warn a customer with a perfectly good card that their
     * subscription cannot renew. An older API omits the key entirely; a real absence sends null.
     */
    cardUnknown() {
      return !!this.summary
        && !Object.prototype.hasOwnProperty.call(this.summary, "paymentMethod");
    },
    // The card lives on the Stripe customer, and the portal answers 409 no_customer before the
    // first purchase (SS06203): there is no card to manage until something has been bought.
    canManageCard() {
      return !this.summary || this.summary.hasCustomer !== false;
    },
    cardLabel() {
      if (this.card) return "Update card";
      return this.cardUnknown ? "Manage card" : "Add a card";
    },
    noCardNote() {
      if (!this.summary) return "";
      if (this.cardUnknown) return "This server does not report card details yet. Stripe has them.";
      if (!this.summary.hasCustomer) return "Nothing has been bought yet, so there is no card to charge.";
      return "Stripe has no card for this workspace. A subscription cannot renew without one.";
    },
    nextInvoice() {
      const next = this.summary && this.summary.nextInvoice;
      if (!next) return null;

      const currency = next.currency || this.summary.currency;
      const lines = (next.lines || []).map(l => ({
        description: this.lineDescription(l),
        amountLabel: this.amount(l.amount, currency),
      }));

      return {
        totalLabel: this.amount(next.total, currency),
        dateLabel: next.date ? this.date(next.date) : "",
        taxLabel: next.tax > 0 ? "Includes " + this.money(next.tax, currency) + " tax" : "",
        lines,
      };
    },
    /**
     * The subscription has no plan line: a Free workspace paying only for seats and/or AutoCount
     * Full. `planless` is the server's name since AutoCount; older servers say `seatOnly`.
     */
    planless() {
      if (!this.summary) return false;
      return this.summary.planless != null ? Boolean(this.summary.planless) : Boolean(this.summary.seatOnly);
    },
    noNextInvoiceNote() {
      if (!this.summary) return "";
      if (this.summary.uncapped) return "Usage on this arrangement is billed outside Stripe.";
      // Free with a plan-less subscription still pays for its other lines.
      if ((this.summary.planKey === "free" && !this.planless) || !this.summary.hasCustomer) {
        return "Nothing is due: this workspace is on Free.";
      }
      if (this.summary.cancelAtPeriodEnd) {
        return "Nothing more is due — this plan ends on "
          + this.date(this.summary.currentPeriodEnd) + ".";
      }
      if (this.summary.currentPeriodEnd) {
        return "The next charge is due around " + this.date(this.summary.currentPeriodEnd)
          + ". A breakdown will appear here once Stripe has drafted it.";
      }
      return "Nothing is scheduled.";
    },
    noDocumentsNote() {
      if (this.documentsFailed) return "We could not load invoices right now. Please try again in a moment.";
      if (this.summary && !this.summary.hasCustomer) {
        return "Nothing has been billed yet. Invoices and top-up receipts will appear here.";
      }
      return "No invoices or receipts yet.";
    },
    regionLabel() {
      const region = (this.summary && this.summary.region) || "US";
      return REGION_NAMES[region] || region;
    },
    currencyLabel() {
      const currency = String((this.summary && this.summary.currency) || "usd").toLowerCase();
      return CURRENCY_NAMES[currency] || currency.toUpperCase();
    },
    regionNote() {
      if (!this.summary) return "";
      if (this.summary.regionConfirmed) {
        return "Set by your first payment and fixed from then on, because it decides the currency every "
          + "later charge is made in. Talk to us if it needs to change.";
      }
      return this.canChooseRegion
        ? "Where your business is registered. It sets the currency for this workspace and is fixed by your first payment."
        : "Worked out from your time zone, and fixed by your first payment.";
    },
    /** Before the first payment a workspace admin names the business country (A-5a); a vendor sets it otherwise. */
    canChooseRegion() {
      return Boolean(this.summary) && !this.summary.regionConfirmed && !this.isManual;
    },
    regionChoices() {
      const current = (this.summary && this.summary.region) || "US";
      return REGION_CHOICES.map((region) => ({ region, label: REGION_NAMES[region], on: region === current }));
    },
    regionGuessLine() {
      return this.summary && this.summary.regionChosen
        ? "Chosen by a workspace admin. You can change it until the first payment."
        : "Worked out from your time zone until you choose. You can change it until the first payment.";
    },
    canCancel() {
      if (!this.summary) return false;
      if (this.summary.planKey === "free" || !this.summary.hasCustomer) return false;
      return this.summary.status === "active"
        || this.summary.status === "past_due"
        || this.summary.status === "trialing"
        || this.summary.status === "unpaid";
    },
    cancelNote() {
      if (this.connectorOnSubscription) {
        return "AutoCount Full is on the same subscription, so the plan is cancelled here and AutoCount Full continues.";
      }
      return "Cancelling is confirmed on Stripe's page, not here.";
    },
    /**
     * Already cancelling: the whole subscription at the period end (the portal's cancel), or just
     * the plan line, which the in-app cancel ends at `planEndsUtc` while the rest carries on.
     */
    planCancelling() {
      return Boolean(this.summary && (this.summary.cancelAtPeriodEnd || this.summary.planEndsUtc));
    },
    cancelHeading() {
      if (this.planCancelling) {
        return "Cancelling on " + this.date(this.summary.planEndsUtc || this.summary.currentPeriodEnd);
      }
      return this.summary.currentPeriodEnd
        ? "Your plan is paid until " + this.date(this.summary.currentPeriodEnd)
        : "Cancel this plan";
    },
    cancelDetail() {
      // What actually happens, in the words of what the code does: the subscription runs to the
      // period end, and the webhook then sets the record to Free. Nothing is deleted -- capacity
      // never blocks work (spec §14 R10) -- and bought credits do not expire.
      return "The plan keeps working until then, and the workspace moves to Free afterwards. "
        + "Nothing is deleted, and credits you have already bought stay."
        + (this.connectorOnSubscription ? " AutoCount Full continues." : "");
    },
    connectors() {
      const list = this.summary && this.summary.connectors;
      return Array.isArray(list) ? list.filter((c) => c && typeof c === "object") : [];
    },
    /** A connector line sits on the subscription, so the plan cannot be cancelled through the portal. */
    connectorOnSubscription() {
      return this.connectors.some((c) => c.onSubscription);
    },
    connectorsNote() {
      return this.isManual
        ? "Connector plans for this workspace go through its vendor too."
        : "Billed on the same subscription and invoice as the plan. Cancelling keeps it on until the end of the period paid for.";
    },
    connectorRows() {
      return this.connectors.map((c, i) => {
        const state = String(c.state || "");
        return {
          key: c.key || "connector-" + i,
          connectorKey: c.key || "autocount",
          name: c.name || "AutoCount Full",
          detail: this.connectorDetail(c, state),
          cancellable: !this.isManual && Boolean(c.onSubscription) && (state === "active" || state === "grace"),
        };
      });
    },
  },
  methods: {
    api() {
      return commonStore.state.client;
    },
    titleCase(value) {
      const text = String(value || "");
      return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
    },
    padMonth(month) {
      return (month < 10 ? "0" : "") + month;
    },
    money(value, currency) {
      return formatMoney(value, currency || (this.summary && this.summary.currency));
    },
    /**
     * An amount on an invoice, including the ones formatMoney refuses: it is built for PRICES,
     * so it returns empty for anything at or below zero. An invoice has both -- a proration
     * credit for unused time is negative, and a fully discounted line is zero -- and a blank
     * cell where a number belongs reads as money quietly missing.
     */
    amount(value, currency) {
      const n = Number(value) || 0;
      const code = currency || (this.summary && this.summary.currency);
      if (n < 0) return "−" + formatMoney(-n, code);
      return n > 0 ? formatMoney(n, code) : this.zero(code);
    },
    zero(currency) {
      const symbols = { usd: "US$", sgd: "S$", myr: "RM" };
      return (symbols[String(currency || "usd").toLowerCase()] || "") + "0";
    },
    date(value) {
      if (!value) return "";
      const d = new Date(value);
      return isNaN(d.getTime())
        ? ""
        : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
    },
    /** Stripe writes "3 × Actor seat (at RM30 / month)"; the quantity is already in the text. */
    lineDescription(line) {
      const text = String(line.description || "").trim();
      if (text) return text;
      return line.quantity > 1 ? line.quantity + " items" : "Subscription";
    },
    statusOf(doc) {
      if (doc.kind === "topup") return { label: "Paid", tone: "is-ok" };
      const status = String(doc.status || "").toLowerCase();
      const label = STATUS_LABELS[status] || this.titleCase(status) || "—";
      const tone = status === "paid" ? "is-ok" : status === "open" ? "is-warn" : "";
      return { label, tone };
    },
    async load() {
      this.loading = true;
      this.unavailable = null;
      try {
        const res = await this.api().get("/api/billing/summary", {
          params: { workspaceId: this.workspaceId },
        });
        this.summary = res.data;
      } catch (err) {
        const status = err && err.response && err.response.status;
        this.unavailable = status === 403
          ? {
              title: "Only a workspace admin can see billing",
              body: "Ask a workspace admin if you need an invoice.",
            }
          : status === 404
            ? {
                title: "Billing is arranged with Inistate",
                body: "Plans are not sold online in this environment. Talk to us and we will set it up.",
              }
            : {
                title: "Billing information is not available right now",
                body: "Please try again in a moment.",
              };
        this.loading = false;
        return;
      }

      if (!this.isManual) await this.loadDocuments();
      this.loading = false;
    },
    async loadDocuments() {
      this.documentsFailed = false;
      try {
        const res = await this.api().get("/api/billing/invoices", {
          params: { workspaceId: this.workspaceId, limit: 24 },
        });
        const items = (res.data && res.data.items) || [];
        this.documents = items.map((doc, i) => {
          const status = this.statusOf(doc);
          return {
            key: doc.number || doc.reference || "doc" + i,
            dateLabel: this.date(doc.date),
            description: doc.description || "Subscription",
            number: doc.number || "",
            amountLabel: this.amount(doc.amount, doc.currency),
            statusLabel: status.label,
            tone: status.tone,
            hostedUrl: doc.hostedUrl || "",
            pdfUrl: doc.pdfUrl || "",
          };
        });
      } catch (err) {
        // Includes a server that predates this endpoint. The summary already answered, so
        // billing is on; this one list is simply not available.
        this.documents = [];
        this.documentsFailed = true;
      }
    },
    /**
     * Every edit is a Stripe Billing Portal session aimed at one task. The server resolves the
     * flow and says which one it opened, because a flow whose ids it does not hold falls back to
     * the portal's home rather than failing.
     */
    async openPortal(flow) {
      if (this.busy) return;
      this.busy = true;
      let inApp = false;
      try {
        const res = await this.api().post("/api/billing/portal", {
          WorkspaceId: this.workspaceId,
          Flow: flow,
        });
        const url = res && res.data && res.data.url;
        if (url) {
          window.location.href = url;
        } else {
          this.$toasted.global.error({}, "The billing portal did not return a link.");
        }
      } catch (err) {
        const data = err && err.response && err.response.data;
        // The subscription gained another line (AutoCount Full, seats) since the page loaded: the
        // portal's cancel would end all of it, so the server sends the plan's cancel back here.
        inApp = flow === "subscription_cancel" && Boolean(data) && data.error === "review_in_app";
        if (!inApp) this.$toasted.global.error(err, (data && data.message) || "Could not open the billing portal.");
      } finally {
        this.busy = false;
      }
      if (inApp) await this.cancelPlanInApp();
    },

    /** The business country before the first payment (A-5a): every price then shows in its currency. */
    async chooseRegion(region) {
      if (this.busy || !this.canChooseRegion || region === this.summary.region) return;
      this.busy = true;
      try {
        await this.api().post("/api/billing/region", { WorkspaceId: Number(this.workspaceId), Region: region });
        await this.reloadSummary();
      } catch (err) {
        const data = err && err.response && err.response.data;
        const code = data && data.error;
        this.$toasted.global.error(err, (data && data.message) || REGION_REFUSALS[code] || "Could not save the country.");
        await this.reloadSummary();
      } finally {
        this.busy = false;
      }
    },

    // ---- connector plans (AutoCount Full) and the plan's own cancel -------------------------------

    /** "RM99/month", or "RM199/month with writes" for the line with writes to the book; "" without a price. */
    connectorPrice(connector) {
      const price = connector.price || {};
      const money = this.money(price.amount, price.currency);
      if (!money) return "";
      const interval = connector.interval || price.interval;
      return money + "/" + (interval === "year" ? "year" : "month") + (connector.writes ? " with writes" : "");
    },
    /** "RM499" while the one-time activation is still to be charged (activation fee doc §3), else "". */
    connectorActivation(connector) {
      const activation = connector.activation || {};
      if (!connector.activationDue || connector.activationPaidUtc) return "";
      return this.money(activation.amount, activation.currency);
    },
    /** One line on what the connector plan is doing now, in the summary's states. */
    connectorDetail(connector, state) {
      const price = this.connectorPrice(connector);
      const activation = this.connectorActivation(connector);
      if (this.isManual || state === "manual") {
        return "Billed by " + (connector.billedBy || (this.summary && this.summary.billedBy) || "your vendor");
      }
      switch (state) {
        case "active": {
          const parts = ["Active", price];
          if (connector.activationPaidUtc) parts.push("activation paid " + this.date(connector.activationPaidUtc));
          else if (activation && connector.activationLinedUp) parts.push("activation " + activation + " on the next invoice");
          return parts.filter(Boolean).join(" · ");
        }
        case "trial": {
          const until = connector.trialEndsUtc ? "Free trial until " + this.date(connector.trialEndsUtc) : "Free trial";
          if (!connector.startAtUtc || !price) return until;
          const from = this.date(connector.startAtUtc);
          return activation
            ? until + ", then " + price + " · activation " + activation + " on " + from
            : until + ", then " + price + " from " + from;
        }
        case "grace":
          return "Payment failed: update the card above to keep it on";
        case "ending":
          return connector.paidThroughUtc
            ? "Cancelled: stays on until " + this.date(connector.paidThroughUtc)
            : "Cancelled: stays on until the end of the period paid for";
        case "payment_failed":
          return "Payment failed: off until the card is updated";
        case "trial_ended":
          return "Trial ended";
        default:
          return price || "Off";
      }
    },
    /**
     * Cancels one connector line in-app: Stripe deletes it without proration, and the plan holds
     * until the end of the period already paid for (no refund, like cancelling a plan).
     */
    async cancelConnector(row) {
      if (this.busy || !row) return;
      const until = this.date(this.summary && this.summary.currentPeriodEnd);
      const confirmed = await this.$confirmation.show({
        type: "confirm",
        title: "Cancel " + row.name + "?",
        text: row.name + " stays on until " + (until || "the end of the period paid for") + ". After that the "
          + "AutoCount modules show a lock. Nothing is deleted, and nothing is refunded. Turning it on again "
          + "later costs only the monthly.",
        buttons: [
          { title: "Keep it", bind: { text: true } },
          {
            title: "Cancel " + row.name,
            bind: { color: "danger" },
            default: true,
            handler: ({ resolve }) => resolve(true),
          },
        ],
      });
      if (confirmed !== true) return;

      this.busy = true;
      try {
        const res = await this.api().post(
          "/api/billing/connectors/" + encodeURIComponent(row.connectorKey) + "/cancel",
          { WorkspaceId: Number(this.workspaceId) }
        );
        const paidThrough = res && res.data && res.data.paidThroughUtc;
        this.$toasted.success(row.name + " cancelled"
          + (paidThrough ? ". It stays on until " + this.date(paidThrough) + "." : "."));
        await this.reloadSummary();
      } catch (err) {
        const data = err && err.response && err.response.data;
        this.$toasted.global.error(err, (data && data.message) || "Could not cancel " + row.name + ".");
      } finally {
        this.busy = false;
      }
    },
    /** The portal's cancel ends the whole subscription, so with a connector on it the plan is cancelled here. */
    cancelPlan() {
      if (this.connectorOnSubscription) return this.cancelPlanInApp();
      return this.openPortal("subscription_cancel");
    },
    /** Deletes only the plan line: the plan holds until the period end, the other lines carry on. */
    async cancelPlanInApp() {
      if (this.busy) return;
      const until = this.date(this.summary && this.summary.currentPeriodEnd);
      const continues = this.connectorOnSubscription
        ? " AutoCount Full continues."
        : " The rest of the subscription continues.";
      const confirmed = await this.$confirmation.show({
        type: "confirm",
        title: "Cancel your plan?",
        text: "The plan keeps working until " + (until || "the end of the period paid for")
          + ", and the workspace moves to Free afterwards." + continues,
        buttons: [
          { title: "Keep the plan", bind: { text: true } },
          {
            title: "Cancel plan",
            bind: { color: "danger" },
            default: true,
            handler: ({ resolve }) => resolve(true),
          },
        ],
      });
      if (confirmed !== true) return;

      this.busy = true;
      try {
        const res = await this.api().post("/api/billing/plan/cancel", { WorkspaceId: Number(this.workspaceId) });
        const ends = res && res.data && res.data.planEndsUtc;
        this.$toasted.success("Plan cancelled" + (ends ? ". It keeps working until " + this.date(ends) + "." : "."));
        await this.reloadSummary();
      } catch (err) {
        const data = err && err.response && err.response.data;
        this.$toasted.global.error(err, (data && data.message) || "Could not cancel the plan.");
      } finally {
        this.busy = false;
      }
    },
    /** The summary again after a change made here, without the page's loading state. */
    async reloadSummary() {
      try {
        const res = await this.api().get("/api/billing/summary", {
          params: { workspaceId: this.workspaceId },
        });
        this.summary = res.data;
      } catch (err) {
        this.$toasted.global.error(err, "Billing could not be refreshed. Reload the page to see the change.");
      }
    },
  },
  watch: {
    workspaceId() {
      this.documents = [];
      this.load();
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style lang="scss" scoped>
/* The business country before the first payment: three real radios drawn as one segmented choice. */
.fts-billing-country {
  min-width: 0;
  max-width: 480px;
  margin: 0;
  padding: 0;
  border: 0;

  legend {
    margin: 0 0 8px;
    padding: 0;
    font-size: 0.8125rem;
    font-weight: 600;
  }
}

.fts-billing-seg {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 3px;
  padding: 3px;
  border: 1px solid var(--fts-settings-line);
  border-radius: 9px;
}

.fts-billing-seg-option {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  margin: 0;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  color: var(--fts-settings-mute);
  cursor: pointer;

  &:hover {
    color: var(--fts-settings-ink);
    background: var(--fts-settings-ground);
  }

  &.is-on,
  &.is-on:hover {
    background: var(--fts-settings-accent);
    color: var(--fts-settings-on-accent);
  }

  &:focus-within {
    outline: 3px solid var(--fts-settings-accent);
    outline-offset: 2px;
  }

  input {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: 0;
    opacity: 0;
    pointer-events: none;
  }
}
</style>
